import { ConnectError, Code } from '@bufbuild/connect';
import { useEffect } from 'react';

import { AdminUserInfoStreamResp_OrganizationUserSharingInfo } from 'protocol/api/core/svc_adminka_pb';
import { OrganizationStaff } from 'protocol/model/dto_organization_staff_pb';

import api from '@/shared/api/api';
import { useAppDispatch } from '@/shared/hooks';

import { userModel } from '@/entities/user';

type UserDataAccumulators = {
  currentUserStaff: OrganizationStaff[];
  sharedOrganizations: AdminUserInfoStreamResp_OrganizationUserSharingInfo[];
};
const dataAccumulators: UserDataAccumulators = {
  currentUserStaff: [],
  sharedOrganizations: [],
};

let abortController: AbortController;

export const useAdminUserInfoStream = (userID: string) => {
  const dispatch = useAppDispatch();

  const openAdminUserInfoStream = async () => {
    abortController = new AbortController();

    dispatch(userModel.actions.setCurrentUserLoadingState('pending'));

    try {
      const adminUserInfoStream = api.core.admin.adminUserInfoStream(
        { UserID: userID },
        { signal: abortController.signal },
      );

      for await (const { Update } of adminUserInfoStream) {
        switch (Update.case) {
          case 'UpdatedUser':
          case 'HistoricalUser': {
            dispatch(userModel.actions.setCurrentUser(Update.value));
            dispatch(userModel.actions.setCurrentUserLoadingState('succeeded'));
            break;
          }

          case 'HistoricalOrganization': {
            dispatch(userModel.actions.addOrganizationName({ id: Update.value.ID, name: Update.value.Name }));
            break;
          }

          case 'HistoricalOrganizationStaff': {
            dataAccumulators.currentUserStaff.push(Update.value);
            break;
          }

          case 'EndOfHistoricalOrganizationStaff': {
            dispatch(
              userModel.actions.setCurrentUserOrganizations(
                dataAccumulators.currentUserStaff,
              ),
            );
            dataAccumulators.currentUserStaff = [];
            break;
          }

          case 'UpdatedOrganizationStaff': {
            dispatch(userModel.actions.setNewestUserOrganization(Update.value));
            break;
          }

          case 'UpdatedSharingInfo': {
            dispatch(userModel.actions.updateOne(Update.value));
            break;
          }
          case 'HistoricalSharingInfo': {
            dataAccumulators.sharedOrganizations.push(Update.value);
            break;
          }

          case 'EndOfHistoricalUserSharings': {
            dispatch(
              userModel.actions.setMany(dataAccumulators.sharedOrganizations),
            );
            dataAccumulators.sharedOrganizations = [];
            break;
          }

          default: {
            // throw
            // throw new Error('Admin User Info Stream failed');
          }
        }
      }
    } catch (error: unknown) {
      if (error instanceof ConnectError && error.code === Code.Canceled) {
        return;
      }
      console.error('AdminUserInfoStream:: error: ', error);
    }
  };

  useEffect(() => {
    if (userID) {
      openAdminUserInfoStream();
    }

    return () => {
      abortController?.abort();

      dispatch(userModel.actions.removeAll({}));
      dispatch(userModel.actions.resetCurrentUser({}));
      dispatch(userModel.actions.resetCurrentUserOrganizations({}));
    };
  }, [userID]);
};
